import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

import PageHeader from "@/components/PageHeader.vue";
import {onMounted, ref} from "vue";
import CategoryService from "@/services/CategoryService";
import {ElMessage} from "element-plus";
import Category from "@/types/Category";
import {useMainStore} from "@/stores/mainStore";
import DocumentService from "@/services/DocumentService";
import UserService from "@/services/UserService";
import User from "@/types/User";
import debounce from "lodash/debounce";

type FormData = {
    subject: string,
    fiscalCode: string,
    subReference: string,
    categoryId: number | null,
    document: File | null,
    notes: string
};

const CHUNK_SIZE = 5 * 1024 * 1024 // 5MB

export default /*@__PURE__*/_defineComponent({
  __name: 'UploadView',
  setup(__props) {

const store = useMainStore();
const form = ref<FormData>({
    subject: "",
    fiscalCode: "",
    subReference: "",
    categoryId: null,
    document: null,
    notes: ""
});
const categories = ref<Category[]>([]);
const isBusy = ref(false);
const isUploading = ref(false);
const isIndeterminate = ref(false);
const uploadProgress = ref(0);
const users = ref<User[]>([]);
const isFiltering = ref(false);

onMounted(async () => {
    try {
        isBusy.value = true;
        const items = await CategoryService.getCategories(store.admin!.organization!.wlextHost, store.admin!.organization!.wlextOrganizationId);
        categories.value = items.map(x => ({
            ...x,
            availabilityDays: store.admin!.organization!.categories.find(y => y.name === x.name)?.availabilityDays ?? 45,
            conditions: store.admin!.organization!.categories.find(y => y.name === x.name)?.conditions ?? [],
            selected: store.admin!.organization!.categories.find(y => y.name === x.name) !== undefined
        }));
        if (categories.value.filter(x => x.selected).length === 1) {
            form.value.categoryId = categories.value.find(x => x.selected)!.id;
        }
    } catch (error: any) {
        ElMessage.error({message: error.message});
    } finally {
        isBusy.value = false;
    }
});

async function loadDocument() {
    document.getElementById('input1')!.click();
}

async function parseDocument(event: Event) {
    console.log((event.target as HTMLInputElement).files);
    form.value.document = (event.target as HTMLInputElement).files?.[0] ?? null;
}

async function upload() {
    if (form.value.document === null ||
        form.value.categoryId === null ||
        form.value.subject === "" ||
        form.value.fiscalCode === "" ||
        form.value.subReference === "") {
        ElMessage.error({ message: "Compilare tutti i campi obbligatori" });
        return;
    }

    try {
        const file: File = form.value.document;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        const id = crypto.randomUUID();

        uploadProgress.value = 0;
        isBusy.value = true;
        isUploading.value = true;
        isIndeterminate.value = false;

        for (let index = 0; index < totalChunks; index++) {
            const start = index * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);

            const blobChunk = file.slice(start, end);
            const arrayBuffer = await blobChunk.arrayBuffer();
            const chunk = arrayBufferToBase64(arrayBuffer);

            await DocumentService.uploadDocumentChunk(store.admin!.organization!.organizationId, id, index, totalChunks, form.value.subject, form.value.fiscalCode.toUpperCase(), form.value.subReference, form.value.categoryId!, chunk, form.value.notes);

            uploadProgress.value = Math.round(((index + 1) / totalChunks) * 100);

            if (index === totalChunks - 2) {
                isIndeterminate.value = true;
            }

            if (index === totalChunks - 1) {
                form.value = {
                    subject: "",
                    fiscalCode: "",
                    subReference: "",
                    categoryId: null,
                    document: null,
                    notes: ""
                };

                ElMessage.success({message: `Upload completato con successo`});
            }
        }
    } catch (error: any) {
        ElMessage.error({message: error.message});
    } finally {
        isBusy.value = false;
        isUploading.value = false;
    }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
}

async function getUsers() {
    isBusy.value = true;
    try {
        users.value = (await UserService.getUsers(1, store.admin!.organization!.id, false)).items;
    } catch (error: any) {
        ElMessage.error({ message: error.message });
    }
    isBusy.value = false;
}

let timer: number | null = null;

function filterUsers(query: string) {
    if (query && query.length >= 3) {
        isFiltering.value = true;

        if (timer)
            clearTimeout(timer);

        timer = setTimeout(async() => {
            try {
                users.value = (await UserService.getUsers(1, store.admin!.organization!.id, false, query)).items;
            } catch (error: any) {
                ElMessage.error({ message: error.message });
            } finally {
                isFiltering.value = false;
            }
        }, 300);
    } else {
        users.value = [];
    }
}

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_progress = _resolveComponent("el-progress")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(PageHeader, null, {
      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
        _createElementVNode("h2", null, "Upload documento", -1)
      ]))]),
      _: 1
    }),
    _createVNode(_component_el_form, {
      model: form.value,
      "label-width": "auto",
      "label-position": "top",
      style: {"max-width":"600px"},
      disabled: isBusy.value
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_form_item, {
          label: "Oggetto",
          required: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: form.value.subject,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.subject) = $event))
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, {
          label: "Codice fiscale",
          required: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_select, {
              modelValue: form.value.fiscalCode,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.fiscalCode) = $event)),
              class: "uppercase",
              placeholder: "Seleziona un paziente",
              "remote-show-suffix": "",
              filterable: "",
              remote: "",
              clearable: "",
              "remote-method": filterUsers,
              loading: isFiltering.value,
              "value-on-clear": null,
              "empty-values": [null]
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(users.value, (user) => {
                  return (_openBlock(), _createBlock(_component_el_option, {
                    key: user.fiscalCode,
                    label: user.fiscalCode,
                    value: user.fiscalCode
                  }, null, 8, ["label", "value"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue", "loading"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, {
          label: "Evento clinico",
          required: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: form.value.subReference,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.subReference) = $event))
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, {
          label: "Categoria documentale",
          required: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_select, {
              modelValue: form.value.categoryId,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.categoryId) = $event)),
              placeholder: "Seleziona una categoria documentale",
              class: "uppercase"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value.filter((x) => x.selected), (category, index) => {
                  return (_openBlock(), _createBlock(_component_el_option, {
                    key: index,
                    label: category.name,
                    value: category.id
                  }, null, 8, ["label", "value"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, {
          label: "Documento",
          required: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: (form.value.document ?? { name: '' }).name,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (((form.value.document ?? { name: '' }).name) = $event)),
              readonly: ""
            }, {
              append: _withCtx(() => [
                _createVNode(_component_el_button, {
                  plain: "",
                  onClick: loadDocument
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(form.value.document === null ?
                                    "Carica..." : "Modifica..."), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"]),
            _createElementVNode("input", {
              id: "input1",
              style: {"display":"none"},
              type: "file",
              onChange: parseDocument
            }, null, 32)
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, { label: "Note" }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: form.value.notes,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.notes) = $event))
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, null, {
          default: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              onClick: upload
            }, {
              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                _createTextVNode("Esegui upload", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }),
        (isUploading.value)
          ? (_openBlock(), _createBlock(_component_el_progress, {
              key: 0,
              "text-inside": true,
              format: (percentage) => isIndeterminate.value ? '' : `${percentage}%`,
              "stroke-width": 16,
              percentage: uploadProgress.value,
              indeterminate: isIndeterminate.value
            }, null, 8, ["format", "percentage", "indeterminate"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["model", "disabled"])
  ]))
}
}

})